// App.jsx
import React, { useState, useEffect, useRef } from 'react';
import {  Route, Routes, useLocation } from 'react-router-dom';

import { Layout, ConfigProvider } from 'antd';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartProvider';
import { SaldoProvider } from './contexts/SaldoContext';
import { GirosGanhosProvider } from './contexts/GirosGanhosContext';

import { FaWhatsapp } from 'react-icons/fa';

import SidebarOfficial from './Components/Sidebar';
import HeaderComponent from './Components/Header';
import FooterComponent from './Components/Footer';

import Login from './Pages/Login';
import Home from './Pages/Home';
import Bolao from './Pages/Bolao';
import Carrinho from './Pages/Carrinho';

import ptBR from 'antd/lib/locale/pt_BR';
import Resultados from './Pages/Resultados';
import Saldo from './Pages/Saldo';
import Avaliacao from './Pages/Avaliacao';
import Conheca from './Pages/Conheca';
import Cadastrar from './Pages/Cadastrar';
import MinhaConta from './Pages/MinhaConta';
import WelcomeMessage from './Components/WelcomeMessage';
import ErrorBoundary from './Components/ErrorBoundary';
import CompraConcluida from './Components/CompraConcluida';

import styles from './App.scss';
import TrocarSenha from './Pages/TrocarSenha';
import PoliticaPrivacidade from './Pages/PoliticaPrivacidade';
import TermoUso from './Pages/TermoUso';
import Roleta from './Pages/Roleta';
import { api } from './services/apiClient';

const { Content, Header, Footer } = Layout;

// -------- Hook de breakpoint (mobile ≤ 1024px)
const useIsMobile = (query = '(max-width: 1024px)') => {
  const getMatch = () => (typeof window === 'undefined' ? false : window.matchMedia(query).matches);
  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler); // fallback
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return matches;
};

// -------- Locale/tema AntD
const customLocale = {
  ...ptBR,
  Pagination: {
    ...ptBR.Pagination,
    items_per_page: '/ página',
    colorPrimary: '#00897F',
  },
};

const tokenConfig = {
  token: {
    colorPrimary: '#00897F',
    colorPrimaryBorder: 'rgba(0, 137, 127, 0.53)',
    colorPrimaryHover: '#00897F',
  },
};

const EmailConfirmation = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      const confirmEmail = async () => {
        try {
          const response = await api.post('/confirma_email', { token });
          console.log('Email confirmado com sucesso:', response.data);
        } catch (error) {
          console.error('Erro ao confirmar o e-mail:', error);
        }
      };
      confirmEmail();
    }
  }, [location.search]);

  return null;
};

const WhatsAppButton = () => {
  return (
    <a
      href='https://wa.me/553492118907'
      target='_blank'
      rel='noopener noreferrer'
      style={{
        position: 'fixed',
        width: '60px',
        height: '60px',
        bottom: '40px',
        right: '40px',
        backgroundColor: '#25D366',
        color: 'white',
        borderRadius: '50px',
        textAlign: 'center',
        fontSize: '30px',
        boxShadow: '2px 2px 3px #999',
        zIndex: 1000,
      }}
    >
      <FaWhatsapp style={{ marginTop: '15px' }} />
    </a>
  );
};

// -------- Rota privada
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, checkAuthentication } = useAuth();

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  if (isAuthenticated === null) {
    return <div>Carregando...</div>;
  }

  return isAuthenticated ? children : <Login />;
};

const App = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const resetStepRef = useRef(() => {});

  // breakpoint mobile
  const isMobileBp = useIsMobile('(max-width: 1024px)');

  // ✅ inicia fechado no mobile, aberto no desktop
  const [collapsed, setCollapsed] = useState(() => (isMobileBp ? true : false));
  const toggleCollapsed = () => setCollapsed((p) => !p);

  // ✅ sincroniza quando mudar o tamanho da tela (rotate/resize)
  useEffect(() => {
    setCollapsed(isMobileBp ? true : false);
  }, [isMobileBp]);

  // ✅ fecha a cada navegação no mobile
  useEffect(() => {
    if (isMobileBp) setCollapsed(true);
  }, [location, isMobileBp]);

  // registrar view quando existir ?localidade=xxx na URL
  useEffect(() => {
    const registerView = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const localidade = params.get('localidade');
        if (localidade) {
          await api.post('/insert-view', {
            parametro: localidade,
            numero_de_views: 1,
          });
          console.log('View registrada ou atualizada com sucesso.');
        }
      } catch (error) {
        console.error('Erro ao registrar a view:', error);
      }
    };
    registerView();
  }, [location]);

  // Opcional: se você usa deviceType em <Home />
  const deviceType = isMobileBp ? 'mobile' : 'desktop';

  return (
    <ErrorBoundary>
      <CartProvider>
        <AuthProvider>
          <SaldoProvider>
            <GirosGanhosProvider>
            <ConfigProvider locale={customLocale} theme={tokenConfig}>
              <div className='appLayout'>
                {/* Sidebar fixa */}
                <SidebarOfficial collapsed={collapsed} toggleCollapsed={toggleCollapsed} userLevel={user ? user.tipoUsuario : 'visitante'} className='sidebar' />

                {/* Coluna principal */}
                <div className='main'>
                  <HeaderComponent collapsed={collapsed} toggleCollapsed={toggleCollapsed} onResetStep={() => resetStepRef.current()} />

                  <main className='mainContent'>
                    <div className='pageInner'>
                      <WelcomeMessage />
                      <EmailConfirmation />

                      <Routes>
                        <Route path='/' element={<Home deviceType={deviceType} />} />
                        <Route path='/bolao/:id?' element={<Bolao />} />
                        <Route path='carrinho' element={<Carrinho onResetStep={(resetStep) => (resetStepRef.current = resetStep)} />} />
                        <Route path='resultados' element={<Resultados />} />
                        <Route path='saldo' element={<Saldo />} />
                        <Route path='avaliacoes' element={<Avaliacao />} />
                        <Route path='conheca' element={<Conheca />} />
                        <Route path='login' element={<Login />} />
                        <Route path='cadastrar' element={<Cadastrar />} />
                        <Route path='alterarsenha' element={<TrocarSenha />} />
                        <Route path='politica-de-privacidade' element={<PoliticaPrivacidade />} />
                        <Route path='termos-de-uso' element={<TermoUso />} />
                        <Route
                          path='minha-conta'
                          element={
                            <PrivateRoute>
                              <MinhaConta />
                            </PrivateRoute>
                          }
                        />
                        <Route path='roleta' element={<Roleta />} />
                        <Route path='/compra_concluida' element={<CompraConcluida />} />
                      </Routes>

                      <ToastContainer />
                      <WhatsAppButton />
                    </div>
                  </main>

                  <FooterComponent />
                </div>
              </div>
            </ConfigProvider>
            </GirosGanhosProvider>
          </SaldoProvider>
        </AuthProvider>
      </CartProvider>
    </ErrorBoundary>
  );
};

export default App;
